import {
  EVDialog,
  EVDialogActions,
  EVDialogContent,
  EVDialogContentText,
  EVDialogTitle,
  EVTextField, EVInputLabel,
  EVCircularProgress,
  makeStyles,
  EVButton,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';
import React, { useState } from 'lib/react';
import useMediaQuery from 'hooks/useMediaQuery';
import { EVReportOutlinedIcon } from '@eagleview/ev-comp-library';
import { useSelector } from 'lib/redux';

const useStyles = makeStyles((theme) => ({
  dialog: {
    width: '450px',
    boxShadow: '0px 24px 38px rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2)',
    borderRadius: '4px',
  },
  title: {
    padding: '0px',
    '&>h3': {
      fontSize: '18px',
      fontWeight: 600,
      letterSpacing: '0em',
      lineHeight: '27px',
      textAlign: 'left',
      color: theme.evPalette.charcoal[900],
      textTransform: 'none',
    },
  },
  contentText1: {
    fontSize: '14px',
    color: theme.evPalette.charcoal[700],
    marginBottom: '0px',
  },
  contentText2: {
    fontSize: '14px',
    color: theme.evPalette.charcoal[900],
    fontWeight: '600',
    marginBottom: '0px',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingBottom: '24px',
    paddingRight: '24px',
    paddingLeft: '24px',
    marginLeft: '0px',
    '&>button': {
      textTransform: 'none',
      fontSize: '16px',
    },
    '@media (min-width: 1024px)': {
      flexDirection: 'row',
      gap: '8px',
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputFieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '11px',
  },
  cancelButtonClass: {
    width: '100%',
    borderColor: theme.evPalette.ocean[600],
    color: theme.evPalette.ocean[600],
    '@media (min-width: 1024px)': {
      width: 'max-content',
      color: theme.evPalette.charcoal[700],
    },
  },
  deleteButtonClass: {
    color: theme.evPalette.raspberry[600],
    '&:hover': {
      color: theme.evPalette.raspberry[600],
    },
    '&.Mui-disabled': {
      color: theme.evPalette.raspberry[100],
    },
    '@media (max-width: 1024px)': {
      width: '100%',
      backgroundColor: theme.evPalette.rose[900],
      color: theme.evPalette.salt[100],
      '&:hover': {
        backgroundColor: theme.evPalette.rose[900],
        color: theme.evPalette.salt[100],
      },
      '&.Mui-disabled': { backgroundColor: theme.evPalette.charcoal[200], color: theme.evPalette.charcoal[600] },
    },
  },
  errorText: {
    fontSize: '12px',
    color: theme.evPalette.rose[900],
    marginBottom: '0px',
  },
}));
function AccountsDeletionConfirmationDialog(props) {
  const {
    open,
    handleClose,
    handleDelete,
  } = props;
  const isMobile = useMediaQuery(640);
  const isTablet = useMediaQuery(1024);
  const classes = useStyles();
  const [inputText, setInputText] = useState('');
  const { t } = useTranslation();
  const { accountDeletionError, deletingAccount } = useSelector(({ accountsDeletion }) => accountsDeletion);

  const handleInputText = (e) => {
    setInputText(e.target.value);
  };
  const handleOkayButtonClick = () => {
    handleDelete();
  };

  return (
    <EVDialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialog }}
    >
      <EVDialogContent className={classes.titleContainer}>
        <EVReportOutlinedIcon htmlColor="#BC1616" />
        <EVDialogTitle disableTypography className={classes.title}>{t('accounts_deletion__delete_account')}</EVDialogTitle>
      </EVDialogContent>
      <EVDialogContent className={classes.textContainer}>
        <EVDialogContentText className={classes.contentText1}>
          {t('accounts_deletion__delete_confirmation_question')}
        </EVDialogContentText>
        <EVDialogContentText className={classes.contentText2}>
          {t('accounts_deletion__delete_warning_text')}
        </EVDialogContentText>
      </EVDialogContent>
      <EVDialogContent className={classes.inputFieldContainer}>
        <EVInputLabel>
          {t('accounts_deletion__please_type')}
          {' '}
          <b>{t('common_delete')}</b>
          {' '}
          {t('accounts_deletion__to_confirm')}
          .
        </EVInputLabel>
        <EVTextField
          required
          value={inputText}
          id="deleteInput"
          name="delete"
          type="text"
          fullWidth
          onChange={handleInputText}
          variant="outlined"
        />
        {!deletingAccount
        && accountDeletionError
        && <EVDialogContentText className={classes.errorText}>{t('accounts_deletion__unable_to_delete')}</EVDialogContentText>}
      </EVDialogContent>
      <EVDialogActions className={classes.actions}>
        <EVButton
          onClick={handleClose}
          variant={isMobile || isTablet ? 'outlined' : 'text'}
          className={classes.cancelButtonClass}
          id="cancel-btn"
        >
          {t('common_cancel')}
        </EVButton>
        <EVButton
          disabled={inputText !== 'Delete'}
          onClick={handleOkayButtonClick}
          variant={isMobile || isTablet ? 'contained' : 'text'}
          className={classes.deleteButtonClass}
          id="confirmDeleteBtn"
          loading={deletingAccount}
          {...((!isMobile && !isTablet) && { LoaderComponent: <EVCircularProgress style={{ height: '24px', width: '24px', color: '#BB115E' }} /> })}
        >
          {t('accounts_deletion__confirm_delete_button_text')}
        </EVButton>
      </EVDialogActions>
    </EVDialog>
  );
}

AccountsDeletionConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleDelete: PropTypes.func,
};

AccountsDeletionConfirmationDialog.defaultProps = {
  handleDelete: null,
};

export default AccountsDeletionConfirmationDialog;
